import { Box, Button, Chip, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  IOrderReturn,
  IOrderReturnConfirmation,
  ReturnConfirmationState,
  ReturnStatus,
} from '../../../types/api/response/returnreceptionresponse';

export const formatReturnDate = (value?: string | null) =>
  value ? new Date(value).toLocaleString() : '—';

/** "2h 05m" style elapsed time since `from`, relative to `now` (ms). */
export const formatElapsed = (from: string | null | undefined, now: number) => {
  if (!from) return '—';
  const start = new Date(from).getTime();
  if (Number.isNaN(start)) return '—';
  const totalMinutes = Math.max(0, Math.floor((now - start) / 60000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  return `${minutes}m`;
};

export const returnStatusColor = (
  status: ReturnStatus,
): 'default' | 'info' | 'success' | 'warning' | 'error' => {
  switch (status) {
    case 'EXPECTED':
      return 'info';
    case 'RECEIVED':
      return 'success';
    case 'SHORT':
      return 'error';
    case 'RESOLVED':
      return 'default';
    default:
      return 'default';
  }
};

export const confirmationStateColor = (
  state: ReturnConfirmationState | null,
): 'default' | 'info' | 'success' | 'error' => {
  switch (state) {
    case 'SENT':
      return 'success';
    case 'PENDING':
      return 'info';
    case 'FAILED':
      return 'error';
    default:
      return 'default';
  }
};

/** Chip label/colour for one confirmation job; a stuck PENDING job is shown as a warning. */
export const confirmationJobChip = (
  job: IOrderReturnConfirmation,
): { label: string; color: 'default' | 'success' | 'warning' | 'error' } => {
  if (job.status === 'PENDING' && job.stuck) return { label: 'Not sent — stuck', color: 'warning' };
  switch (job.status) {
    case 'SENT':
      return { label: 'SENT', color: 'success' };
    case 'FAILED':
      return { label: 'FAILED', color: 'error' };
    default:
      return { label: job.status, color: 'default' };
  }
};

/** Latest (newest) confirmation job, or null if none — `confirmations` is oldest first. */
export const latestConfirmation = (orderReturn: IOrderReturn): IOrderReturnConfirmation | null =>
  orderReturn.confirmations?.length
    ? orderReturn.confirmations[orderReturn.confirmations.length - 1]
    : null;

/** Why a return is on the "Confirmation failures" tab. */
export const confirmationFailureReason = (
  orderReturn: IOrderReturn,
): { label: string; color: 'warning' | 'error' } | null => {
  const jobs = orderReturn.confirmations ?? [];
  if (jobs.some((j) => j.status === 'FAILED')) return { label: 'Failed', color: 'error' };
  if (jobs.some((j) => j.stuck)) return { label: 'Not sent — stuck', color: 'warning' };
  return null;
};

const driverColumn: GridColDef = {
  field: 'driver_name',
  headerName: 'Driver',
  flex: 1,
  minWidth: 160,
  renderCell: ({ row }: GridRenderCellParams<IOrderReturn>) => (
    <Box>
      <Typography variant="body2">{row.driver_name ?? '—'}</Typography>
      {row.driver_phone && (
        <Typography variant="caption" color="text.secondary">
          {row.driver_phone}
        </Typography>
      )}
    </Box>
  ),
};

const orderNumberColumn: GridColDef = {
  field: 'order_number',
  headerName: 'Order No',
  flex: 1,
  minWidth: 140,
};

const returnRefColumn: GridColDef = {
  field: 'return_ref',
  headerName: 'Return Ref',
  flex: 0.8,
  minWidth: 120,
};

const statusColumn: GridColDef = {
  field: 'status',
  headerName: 'Status',
  flex: 0.6,
  minWidth: 110,
  renderCell: ({ value }: GridRenderCellParams) => (
    <Chip label={value} size="small" color={returnStatusColor(value)} />
  ),
};

const sourceColumn: GridColDef = {
  field: 'source',
  headerName: 'Source',
  flex: 0.7,
  minWidth: 110,
  renderCell: ({ value }: GridRenderCellParams) => (
    <Chip label={value} size="small" color={value === 'SUPERPRO' ? 'secondary' : 'default'} />
  ),
};

const receivedAtColumn: GridColDef = {
  field: 'received_at',
  headerName: 'Received At',
  flex: 1,
  minWidth: 170,
  valueFormatter: (value: string | null) => formatReturnDate(value),
};

const receivedByColumn: GridColDef = {
  field: 'received_by',
  headerName: 'Received By',
  flex: 0.8,
  minWidth: 130,
  valueFormatter: (value: string | null) => value ?? '—',
};

export const getExpectedReturnsColumns = (now: number): GridColDef[] => [
  orderNumberColumn,
  driverColumn,
  {
    field: 'tour_id',
    headerName: 'Tour',
    flex: 1,
    minWidth: 160,
    valueFormatter: (value: string | null) => value ?? '—',
  },
  {
    field: 'queued_at',
    headerName: 'Queued At',
    flex: 1,
    minWidth: 170,
    valueFormatter: (value: string | null) => formatReturnDate(value),
  },
  {
    field: 'since_queued',
    headerName: 'Time Since Queued',
    flex: 0.8,
    minWidth: 150,
    sortable: false,
    renderCell: ({ row }: GridRenderCellParams<IOrderReturn>) => (
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" fontWeight={row.overdue ? 700 : undefined}>
          {formatElapsed(row.queued_at, now)}
        </Typography>
        {row.overdue && <Chip label="Overdue" size="small" color="warning" />}
      </Box>
    ),
  },
];

export const getShortReturnsColumns = (
  onResolve: (orderReturn: IOrderReturn) => void,
): GridColDef[] => [
  returnRefColumn,
  orderNumberColumn,
  sourceColumn,
  driverColumn,
  receivedAtColumn,
  receivedByColumn,
  {
    field: 'remark',
    headerName: 'Remark',
    flex: 1.5,
    minWidth: 200,
    valueFormatter: (value: string | null) => value ?? '—',
  },
  {
    field: 'actions',
    headerName: '',
    minWidth: 110,
    sortable: false,
    renderCell: ({ row }: GridRenderCellParams<IOrderReturn>) => (
      <Button
        size="small"
        variant="outlined"
        color="warning"
        onClick={(e) => {
          e.stopPropagation();
          onResolve(row);
        }}
      >
        Resolve
      </Button>
    ),
  },
];

export const getFailedConfirmationsColumns = (
  onResend: (orderReturn: IOrderReturn) => void,
): GridColDef[] => [
  returnRefColumn,
  orderNumberColumn,
  statusColumn,
  driverColumn,
  {
    field: 'tour_id',
    headerName: 'Tour',
    flex: 1,
    minWidth: 160,
    valueFormatter: (value: string | null) => value ?? '—',
  },
  receivedAtColumn,
  {
    field: 'failure_reason',
    headerName: 'Problem',
    flex: 0.8,
    minWidth: 140,
    sortable: false,
    renderCell: ({ row }: GridRenderCellParams<IOrderReturn>) => {
      const reason = confirmationFailureReason(row);
      return reason ? <Chip label={reason.label} size="small" color={reason.color} /> : '—';
    },
  },
  {
    field: 'latest_confirmation',
    headerName: 'Latest Event',
    flex: 1,
    minWidth: 150,
    sortable: false,
    renderCell: ({ row }: GridRenderCellParams<IOrderReturn>) => {
      const job = latestConfirmation(row);
      return job ? `${job.event} (${job.attempts} attempts)` : '—';
    },
  },
  {
    field: 'actions',
    headerName: '',
    minWidth: 110,
    sortable: false,
    renderCell: ({ row }: GridRenderCellParams<IOrderReturn>) => (
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          onResend(row);
        }}
      >
        Resend
      </Button>
    ),
  },
];

/** History of every return; INTERNAL ones (bay intake) have no SuperPro confirmation. */
export const getAllReturnsColumns = (): GridColDef[] => [
  returnRefColumn,
  orderNumberColumn,
  sourceColumn,
  statusColumn,
  driverColumn,
  receivedAtColumn,
  receivedByColumn,
  {
    field: 'confirmation_state',
    headerName: 'Confirmation',
    flex: 0.7,
    minWidth: 120,
    sortable: false,
    renderCell: ({ value }: GridRenderCellParams) =>
      value ? (
        <Chip label={value} size="small" color={confirmationStateColor(value)} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      ),
  },
];
